// Reports → Cost by state (PRD TX2): projects down, work states across, in loaded cost, wages or hours.
// A project opens to its pay types (FTE, Partner, 1099), and each pay type to its people. Each row's
// bar shows how its total splits across the states. Pay data: needs comp.read and period.close.

import { Fragment, useMemo, useState } from "react";
import { Button, Caption1, Card, Field, Select, Subtitle2, Switch } from "@fluentui/react-components";
import { NO_STATE, today, WORKER_GROUPS, type StateCostRow, type WorkerGroup } from "../../../core";
import { api, errorText, useApi } from "../api";
import { DateInput } from "../DateInput";
import { href } from "../router";
import { money, pct } from "../format";
import { ErrorBar, Kpi, Loading } from "../ui";
import { colorsFor } from "./Charts";
import { download } from "./Expenses";

interface StateReportResponse {
  from: string;
  to: string;
  rows: StateCostRow[];
  people: { id: string; name: string }[];
  codes: { code: string; client: string; billable: boolean }[];
  canExport: boolean;
}

type Measure = "cost" | "wages" | "hours";
const MEASURES: { key: Measure; label: string }[] = [
  { key: "cost", label: "Loaded cost" },
  { key: "wages", label: "Wages (FTE pay only)" },
  { key: "hours", label: "Hours" },
];

const hrs = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 1 });
const stateLabel = (s: string) => (s === NO_STATE ? "No work state" : s);

export function StateReport() {
  const year = today().slice(0, 4);
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(today());
  const [measure, setMeasure] = useState<Measure>("cost");
  const [project, setProject] = useState("");
  const [group, setGroup] = useState<WorkerGroup | "">("");
  const [billableOnly, setBillableOnly] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [exportError, setExportError] = useState<string>();
  const data = useApi<StateReportResponse>(from && to && from <= to ? `/reports/states?from=${from}&to=${to}` : null);
  const d = data.data;

  const names = useMemo(() => new Map(d?.people.map((p) => [p.id, p.name]) ?? []), [d]);
  const codeInfo = useMemo(() => new Map(d?.codes.map((c) => [c.code, c]) ?? []), [d]);
  const rows = useMemo(
    () =>
      (d?.rows ?? []).filter(
        (r) => (!project || r.code === project) && (!group || r.group === group) && (!billableOnly || codeInfo.get(r.code)?.billable),
      ),
    [d, project, group, billableOnly, codeInfo],
  );
  const value = (r: StateCostRow) => r[measure];
  const fmt = (n: number) => (measure === "hours" ? hrs(n) : money(n));

  // States ordered by their total, largest first, with a color each.
  const stateTotals = useMemo(() => {
    const t = new Map<string, number>();
    for (const r of rows) t.set(r.state, (t.get(r.state) ?? 0) + value(r));
    return [...t.entries()].filter(([, v]) => v !== 0).sort((a, b) => b[1] - a[1]);
  }, [rows, measure]);
  const states = stateTotals.map(([s]) => s);
  const colors = colorsFor(states, (s) => s === NO_STATE);
  const grand = stateTotals.reduce((t, [, v]) => t + v, 0);

  // Project → pay type → person, each with a total per state.
  const tree = useMemo(() => {
    type Node = { key: string; label: string; byState: Map<string, number>; total: number; children: Node[] };
    const node = (key: string, label: string): Node => ({ key, label, byState: new Map(), total: 0, children: [] });
    const add = (n: Node, r: StateCostRow) => {
      n.byState.set(r.state, (n.byState.get(r.state) ?? 0) + value(r));
      n.total += value(r);
    };
    const projects = new Map<string, Node>();
    for (const r of rows) {
      const code = r.code || "";
      const p = projects.get(code) ?? node(code, code || "No project code");
      projects.set(code, p);
      let g = p.children.find((c) => c.key === `${code}|${r.group}`);
      if (!g) p.children.push((g = node(`${code}|${r.group}`, r.group)));
      let person = g.children.find((c) => c.key === `${code}|${r.group}|${r.personId}`);
      if (!person) g.children.push((person = node(`${code}|${r.group}|${r.personId}`, names.get(r.personId) ?? r.personId)));
      add(p, r);
      add(g, r);
      add(person, r);
    }
    const order = (ns: Node[], byLabel = false) => ns.sort((a, b) => (byLabel ? a.label.localeCompare(b.label) : b.total - a.total));
    const list = order([...projects.values()], true);
    for (const p of list) {
      p.children.sort((a, b) => WORKER_GROUPS.indexOf(a.label as WorkerGroup) - WORKER_GROUPS.indexOf(b.label as WorkerGroup));
      for (const g of p.children) order(g.children);
    }
    return list.filter((p) => p.total !== 0);
  }, [rows, measure, names]);

  const toggle = (key: string) =>
    setOpen((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const allKeys = tree.flatMap((p) => [p.key, ...p.children.map((g) => g.key)]);

  const exportCsv = async () => {
    setExportError(undefined);
    try {
      download(await api<{ name: string; contentType: string; data: string }>(`/reports/states/export?from=${from}&to=${to}`));
    } catch (err) {
      setExportError(errorText(err));
    }
  };

  const Mix = ({ byState, total }: { byState: Map<string, number>; total: number }) =>
    total > 0 ? (
      <div className="state-mix" aria-label={states.map((s) => `${stateLabel(s)} ${pct((byState.get(s) ?? 0) / total, 0)}`).join(", ")}>
        {states.map((s) => {
          const share = (byState.get(s) ?? 0) / total;
          return share > 0 ? (
            <span key={s} style={{ width: `${share * 100}%`, background: colors.get(s) }} title={`${stateLabel(s)}: ${pct(share, 0)}`}>
              {share >= 0.12 ? pct(share, 0) : ""}
            </span>
          ) : null;
        })}
      </div>
    ) : null;

  const cells = (byState: Map<string, number>, total: number) => (
    <>
      {states.map((s) => (
        <td key={s} className="num">
          {byState.get(s) ? fmt(byState.get(s)!) : ""}
        </td>
      ))}
      <td className="num">
        <strong>{fmt(total)}</strong>
      </td>
      <td className="mix-cell">
        <Mix byState={byState} total={total} />
      </td>
    </>
  );

  return (
    <Card className="panel wide">
      <Subtitle2>Cost by state</Subtitle2>
      <Caption1 className="block muted">
        Every counted hour, in the state the person worked that day (their work state, or a travel record), on the project it was reported to, at the
        person's actual rate for the month. Wages are FTE pay without employer taxes or benefits; partners and 1099s have cost only. Cost a salaried
        person's projects don't carry because fewer hours were reported than expected isn't here. Work states and travel are set on each person card;
        hours by person and state, with travel days, are under <a href={href("actuals", "states")}>Time Keeping → Work states</a>.
      </Caption1>
      <div className="toolbar">
        <Field label="From">
          <DateInput size="small" type="date" value={from} onChange={(_, x) => x.value && setFrom(x.value)} />
        </Field>
        <Field label="To">
          <DateInput size="small" type="date" value={to} onChange={(_, x) => x.value && setTo(x.value)} />
        </Field>
        <Field label="Show">
          <Select size="small" value={measure} onChange={(_, v) => setMeasure(v.value as Measure)}>
            {MEASURES.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Project">
          <Select size="small" value={project} onChange={(_, v) => setProject(v.value)}>
            <option value="">All</option>
            {[...new Set(d?.rows.map((r) => r.code) ?? [])].sort().map((c) => (
              <option key={c} value={c}>
                {c ? `${c}${codeInfo.get(c)?.client ? ` · ${codeInfo.get(c)!.client}` : ""}` : "No project code"}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Type">
          <Select size="small" value={group} onChange={(_, v) => setGroup(v.value as WorkerGroup | "")}>
            <option value="">All</option>
            {WORKER_GROUPS.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </Select>
        </Field>
        <Switch label="Billable projects only" checked={billableOnly} onChange={(_, v) => setBillableOnly(v.checked)} />
        {d?.canExport && (
          <Button size="small" onClick={() => void exportCsv()}>
            Export for Excel
          </Button>
        )}
      </div>
      <ErrorBar message={data.error ?? exportError} />
      {!d ? (
        data.loading && <Loading />
      ) : !tree.length ? (
        <Caption1>Nothing counted in these dates{project || group || billableOnly ? " with these filters" : ""}.</Caption1>
      ) : (
        <>
          <div className="kpis" aria-label="By state">
            <Kpi label="Total" value={fmt(grand)} />
            {stateTotals.map(([s, v]) => (
              <Kpi key={s} label={stateLabel(s)} value={fmt(v)} sub={grand ? pct(v / grand) : undefined} flag={s === NO_STATE ? "Set a work state on the person card" : undefined} />
            ))}
          </div>
          <div className="state-legend">
            {states.map((s) => (
              <span key={s}>
                <i style={{ background: colors.get(s) }} />
                {stateLabel(s)}
              </span>
            ))}
            <Button size="small" appearance="subtle" onClick={() => setOpen(open.size ? new Set() : new Set(allKeys))}>
              {open.size ? "Collapse all" : "Expand all"}
            </Button>
          </div>
          <div className="table-scroll">
            <table className="edit-table state-table">
              <thead>
                <tr>
                  <th>Project</th>
                  {states.map((s) => (
                    <th key={s} className="num">
                      {stateLabel(s)}
                    </th>
                  ))}
                  <th className="num">Total</th>
                  <th>Split by state</th>
                </tr>
              </thead>
              <tbody>
                {tree.map((p) => (
                  <Fragment key={p.key}>
                    <tr className="state-project">
                      <td>
                        <button className="tree-toggle" aria-expanded={open.has(p.key)} onClick={() => toggle(p.key)}>
                          {open.has(p.key) ? "⊟" : "⊞"} <strong>{p.label}</strong>
                        </button>
                        {p.key && codeInfo.get(p.key)?.client && <Caption1 className="muted"> {codeInfo.get(p.key)!.client}</Caption1>}
                      </td>
                      {cells(p.byState, p.total)}
                    </tr>
                    {open.has(p.key) &&
                      p.children.map((g) => (
                        <Fragment key={g.key}>
                          <tr className="state-group">
                            <td>
                              <button className="tree-toggle indent-1" aria-expanded={open.has(g.key)} onClick={() => toggle(g.key)}>
                                {open.has(g.key) ? "⊟" : "⊞"} <strong>{g.label}</strong>
                              </button>
                            </td>
                            {cells(g.byState, g.total)}
                          </tr>
                          {open.has(g.key) &&
                            g.children.map((person) => (
                              <tr key={person.key} className="state-person">
                                <td>
                                  <span className="indent-2">{person.label}</span>
                                </td>
                                {cells(person.byState, person.total)}
                              </tr>
                            ))}
                        </Fragment>
                      ))}
                  </Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="state-total">
                  <td>
                    <strong>Total</strong>
                  </td>
                  {cells(new Map(stateTotals), grand)}
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}
